import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Building2,
  RefreshCw,
  Upload,
  Trash2,
  Plus,
  Download,
  Users,
  UserPlus,
  Search,
  Loader2,
  Pencil,
  Image,
  FileText,
  Mail,
  MessageSquare,
  Settings,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = ["Nuova", "In valutazione", "SOPRALLUOGO", "TRATTATIVA", "CHIUSO"];

const emptyForm = {
  name: "",
  status: "Nuova",
  location: "",
  address: "",
  sqm: "",
  rent_range: "Da definire",
  prezzo_vendita: "",
  note: "",
  provincia: "",
  tipo: "",
  posti_letto: "",
  is_active: true,
};

function photoUrl(photo) {
  const url = photo?.preview_url || photo?.url;
  if (!url) return null;
  return url.startsWith("/") ? `${API}${url}` : url;
}

const ALL_TABS = ["immobili", "richieste", "impostazioni", "accesso", "portale"];

export default function ImmobiliAdminModule({ onRefresh, allowedTabs = null }) {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}` };
  const tabs = Array.isArray(allowedTabs) && allowedTabs.length
    ? ALL_TABS.filter((t) => allowedTabs.includes(t))
    : ALL_TABS;
  const defaultTab = tabs[0] || "immobili";
  const canCatalog = tabs.includes("immobili");
  const canRichieste = tabs.includes("richieste");
  const canConfig = tabs.includes("impostazioni");
  const canAccess = tabs.includes("accesso");
  const canViewers = tabs.includes("portale");

  const [properties, setProperties] = useState([]);
  const [accessUsers, setAccessUsers] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchAccess, setSearchAccess] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editPhotos, setEditPhotos] = useState([]);
  const [editPlanimetria, setEditPlanimetria] = useState(null);
  const [editZohoId, setEditZohoId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerForm, setViewerForm] = useState({
    email: "",
    password: "",
    nome: "",
    cognome: "",
    sendAccessEmail: false,
  });
  const [config, setConfig] = useState({ notification_email: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [richieste, setRichieste] = useState([]);
  const [richiesteStats, setRichiesteStats] = useState({ nuovo: 0, total: 0 });
  const [richiesteFilter, setRichiesteFilter] = useState("all");
  const [selectedRichiesta, setSelectedRichiesta] = useState(null);
  const [sendingAccessEmailId, setSendingAccessEmailId] = useState(null);
  const [accessEmailOpen, setAccessEmailOpen] = useState(false);
  const [accessEmailViewer, setAccessEmailViewer] = useState(null);
  const [accessEmailPassword, setAccessEmailPassword] = useState("");
  const editPhotoInputRef = useRef(null);
  const editPlanInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const tasks = [];
      if (canCatalog) tasks.push(["props", fetch(`${API}/api/admin/immobili`, { headers })]);
      if (canAccess) tasks.push(["access", fetch(`${API}/api/admin/immobili/access/users`, { headers })]);
      if (canViewers) tasks.push(["viewers", fetch(`${API}/api/admin/immobili/viewers`, { headers })]);
      if (canConfig) tasks.push(["config", fetch(`${API}/api/admin/immobili/config`, { headers })]);
      if (canRichieste) {
        tasks.push(["richieste", fetch(`${API}/api/admin/immobili/richieste`, { headers })]);
        tasks.push(["stats", fetch(`${API}/api/admin/immobili/richieste/stats`, { headers })]);
      }
      const results = await Promise.all(tasks.map(async ([key, p]) => [key, await p]));
      for (const [key, res] of results) {
        if (!res.ok) continue;
        if (key === "props") {
          const j = await res.json();
          setProperties(j.data || []);
        } else if (key === "access") {
          const j = await res.json();
          setAccessUsers(j.data || []);
        } else if (key === "viewers") {
          const j = await res.json();
          setViewers(j.data || []);
        } else if (key === "config") {
          setConfig(await res.json());
        } else if (key === "richieste") {
          const j = await res.json();
          setRichieste(j.data || []);
        } else if (key === "stats") {
          setRichiesteStats(await res.json());
        }
      }
    } catch {
      toast.error("Errore caricamento dati immobili");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const refreshPropertyInEdit = async (propId) => {
    const res = await fetch(`${API}/api/admin/immobili/${propId}`, { headers });
    if (!res.ok) return;
    const property = await res.json();
    setEditPhotos(property.photos || []);
    setEditPlanimetria(
      property.planimetria_url
        ? { name: property.planimetria_name, url: property.planimetria_url }
        : null
    );
    setProperties((prev) => prev.map((p) => (p.id === propId ? property : p)));
  };

  const handleSyncZoho = async (forceMedia = false) => {
    toast.info("Sincronizzazione Zoho disabilitata — gestisci gli immobili solo su Affidya");
  };

  const openCreate = () => {
    setEditId(null);
    setEditForm(emptyForm);
    setEditPhotos([]);
    setEditPlanimetria(null);
    setEditZohoId(null);
    setEditOpen(true);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setEditForm({
      name: p.name || "",
      status: p.status || "Nuova",
      location: p.location || "",
      address: p.address || "",
      sqm: p.sqm ?? "",
      rent_range: p.rent_range || "Da definire",
      prezzo_vendita: p.prezzo_vendita ?? "",
      note: p.note || "",
      provincia: p.provincia || "",
      tipo: p.tipo || "",
      posti_letto: p.posti_letto ?? "",
      is_active: p.is_active !== false,
    });
    setEditPhotos(p.photos || []);
    setEditPlanimetria(
      p.planimetria_url ? { name: p.planimetria_name, url: p.planimetria_url } : null
    );
    setEditZohoId(p.zoho_record_id || null);
    setEditOpen(true);
  };

  const saveProperty = async () => {
    const payload = {
      ...editForm,
      sqm: editForm.sqm === "" ? 0 : Number(editForm.sqm),
      prezzo_vendita: editForm.prezzo_vendita === "" ? null : Number(editForm.prezzo_vendita),
      posti_letto: editForm.posti_letto === "" ? null : Number(editForm.posti_letto),
    };
    try {
      const url = editId ? `${API}/api/admin/immobili/${editId}` : `${API}/api/admin/immobili`;
      const res = await fetch(url, {
        method: editId ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      if (!editId) {
        setEditId(json.id);
        setEditPhotos(json.photos || []);
        toast.success("Immobile creato. Ora puoi aggiungere foto e planimetria.");
      } else {
        toast.success("Immobile aggiornato");
        setEditOpen(false);
      }
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm("Eliminare questo immobile?")) return;
    try {
      const res = await fetch(`${API}/api/admin/immobili/${id}`, { method: "DELETE", headers });
      if (!res.ok) {
        const j = await res.json();
        throw new Error(j.detail || "Eliminazione fallita");
      }
      toast.success("Immobile eliminato");
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const uploadPhotos = async (propId, files) => {
    if (!files?.length) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(`${API}/api/admin/immobili/${propId}/photos`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || `Upload fallito: ${file.name}`);
      }
      toast.success(files.length > 1 ? `${files.length} foto caricate` : "Foto caricata e ottimizzata");
      await refreshPropertyInEdit(propId);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async (propId, photoId) => {
    try {
      const res = await fetch(`${API}/api/admin/immobili/${propId}/photos/${photoId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Eliminazione fallita");
      toast.success("Foto eliminata");
      await refreshPropertyInEdit(propId);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const uploadPlanimetria = async (propId, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${API}/api/admin/immobili/${propId}/planimetria`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Upload fallito");
      toast.success("Planimetria caricata");
      await refreshPropertyInEdit(propId);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const syncPropertyMedia = async (propId) => {
    setUploadingPhoto(true);
    try {
      const res = await fetch(`${API}/api/admin/immobili/${propId}/sync-media`, {
        method: "POST",
        headers,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Reimport fallito");
      toast.success(`Media aggiornati (${json.property?.photos?.length || 0} foto)`);
      if (json.errors?.length) toast.warning(`${json.errors.length} avvisi durante il download`);
      await refreshPropertyInEdit(propId);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const toggleAccess = async (userId, enabled) => {
    try {
      const res = await fetch(`${API}/api/admin/immobili/access/users/${userId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ immobiliare_enabled: enabled }),
      });
      if (!res.ok) throw new Error("Aggiornamento fallito");
      setAccessUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, immobiliare_enabled: enabled } : u))
      );
    } catch {
      toast.error("Errore aggiornamento accesso");
    }
  };

  const createViewer = async () => {
    const { sendAccessEmail, ...payload } = viewerForm;
    try {
      const res = await fetch(`${API}/api/admin/immobili/viewers`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Creazione fallita");
      if (sendAccessEmail) {
        const mailRes = await fetch(`${API}/api/admin/immobili/viewers/${json.id}/send-access-email`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ password: viewerForm.password }),
        });
        const mailJson = await mailRes.json().catch(() => ({}));
        if (!mailRes.ok) {
          toast.error(mailJson.detail || "Utente creato ma invio email fallito");
        } else {
          toast.success(mailJson.message || `Utente creato e email inviata a ${viewerForm.email}`);
        }
      } else {
        toast.success("Utente portale creato");
      }
      setViewerOpen(false);
      setViewerForm({ email: "", password: "", nome: "", cognome: "", sendAccessEmail: false });
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteViewer = async (id) => {
    if (!window.confirm("Eliminare questo utente portale?")) return;
    try {
      const res = await fetch(`${API}/api/admin/immobili/viewers/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Eliminazione fallita");
      toast.success("Utente eliminato");
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openSendAccessEmailDialog = (viewer) => {
    setAccessEmailViewer(viewer);
    setAccessEmailPassword("");
    setAccessEmailOpen(true);
  };

  const sendViewerAccessEmail = async () => {
    if (!accessEmailViewer) return;
    if (!accessEmailPassword) {
      toast.error("Inserisci la password impostata per l'utente");
      return;
    }
    setSendingAccessEmailId(accessEmailViewer.id);
    try {
      const res = await fetch(
        `${API}/api/admin/immobili/viewers/${accessEmailViewer.id}/send-access-email`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ password: accessEmailPassword }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Invio email fallito");
      toast.success(json.message || `Email inviata a ${accessEmailViewer.email}`);
      setAccessEmailOpen(false);
      setAccessEmailViewer(null);
      setAccessEmailPassword("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingAccessEmailId(null);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API}/api/admin/immobili/config`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ notification_email: config.notification_email }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      setConfig({
        notification_email: json.notification_email,
        updated_at: json.updated_at,
      });
      toast.success("Email notifiche salvata");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const updateRichiestaStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/immobili/richieste/${id}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Aggiornamento fallito");
      toast.success(status === "gestito" ? "Segnata come gestita" : "Segnata come letta");
      setSelectedRichiesta(null);
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT");
    } catch {
      return iso;
    }
  };

  const filteredRichieste = richieste.filter((r) => {
    if (richiesteFilter === "all") return true;
    return r.status === richiesteFilter;
  });

  const filteredAccess = accessUsers.filter(
    (u) =>
      !searchAccess ||
      u.email?.toLowerCase().includes(searchAccess.toLowerCase()) ||
      u.name?.toLowerCase().includes(searchAccess.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Building2}
        title="Gestione Immobiliare"
        description="Catalogo immobili su Affidya con foto ottimizzate (anteprima + versione grande). Gestione solo su Affidya."
        actions={
        <>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleSyncZoho(false)}
            title="Sincronizzazione Zoho disabilitata"
          >
            <Download className="w-4 h-4 mr-1" />
            Sync dati Zoho
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleSyncZoho(true)}
            title="Sincronizzazione Zoho disabilitata"
          >
            <Image className="w-4 h-4 mr-1" />
            Reimporta foto Zoho
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Nuovo immobile
          </Button>
        </>
        }
      />

      <Tabs defaultValue={defaultTab} key={defaultTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          {canCatalog && (
            <TabsTrigger value="immobili">Immobili ({properties.length})</TabsTrigger>
          )}
          {canRichieste && (
            <TabsTrigger value="richieste" className="relative">
              Richieste
              {richiesteStats.nuovo > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0 min-w-[1.25rem]">
                  {richiesteStats.nuovo}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {canConfig && <TabsTrigger value="impostazioni">Impostazioni</TabsTrigger>}
          {canAccess && <TabsTrigger value="accesso">Accesso utenti</TabsTrigger>}
          {canViewers && <TabsTrigger value="portale">Utenti portale</TabsTrigger>}
        </TabsList>

        {canCatalog && (
        <TabsContent value="immobili" className="mt-4">
          <div className="grid gap-4">
            {properties.map((p) => {
              const cover = p.photos?.length > 0 ? photoUrl(p.photos[0]) : null;
              return (
                <Card key={p.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col sm:flex-row">
                      <div
                        className="sm:w-40 h-32 sm:h-auto shrink-0 bg-orange-100 flex items-center justify-center"
                        style={
                          cover
                            ? {
                                backgroundImage: `url(${cover})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }
                            : undefined
                        }
                      >
                        {!cover && <Building2 className="w-10 h-10 text-orange-300" />}
                      </div>
                      <div className="flex-1 p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{p.name || p.address || p.location}</p>
                          <p className="text-sm text-slate-500 mt-1">
                            {p.location} · {p.rent_range}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="secondary" className="text-xs">
                              <Image className="w-3 h-3 mr-1" />
                              {p.photos?.length || 0} foto
                            </Badge>
                            {p.planimetria_url && (
                              <Badge variant="outline" className="text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                Planimetria
                              </Badge>
                            )}
                            {p.zoho_record_id && (
                              <Badge variant="outline" className="text-xs">Zoho</Badge>
                            )}
                            {!p.is_active && (
                              <Badge variant="destructive" className="text-xs">Non attivo</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(p)}>
                            <Pencil className="w-4 h-4 mr-1" />
                            Modifica
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteProperty(p.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {properties.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  Nessun immobile. Usa &quot;Sync dati Zoho&quot; o creane uno nuovo.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        )}

        {canRichieste && (
        <TabsContent value="richieste" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-3">
              <Card className="px-4 py-3">
                <p className="text-2xl font-bold text-red-600">{richiesteStats.nuovo}</p>
                <p className="text-xs text-slate-500">Nuove</p>
              </Card>
              <Card className="px-4 py-3">
                <p className="text-2xl font-bold text-slate-700">{richiesteStats.total}</p>
                <p className="text-xs text-slate-500">Totali</p>
              </Card>
            </div>
            <Select value={richiesteFilter} onValueChange={setRichiesteFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtra" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="nuovo">Solo nuove</SelectItem>
                <SelectItem value="letto">Lette</SelectItem>
                <SelectItem value="gestito">Gestite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            {filteredRichieste.map((r) => (
              <Card
                key={r.id}
                className={`cursor-pointer transition-colors hover:border-orange-200 ${
                  r.status === "nuovo" ? "border-orange-200 bg-orange-50/30" : ""
                }`}
                onClick={() => setSelectedRichiesta(r)}
              >
                <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{r.property_name || "Immobile"}</p>
                      <Badge
                        variant={r.status === "nuovo" ? "default" : "secondary"}
                        className={r.status === "nuovo" ? "bg-orange-600" : ""}
                      >
                        {r.status === "nuovo" ? "Nuova" : r.status === "gestito" ? "Gestita" : "Letta"}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {r.nome} · {r.email}
                      {r.telefono ? ` · ${r.telefono}` : ""}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {r.motivo_label} · {r.struttura} · {formatDate(r.created_at)}
                    </p>
                  </div>
                  <MessageSquare className="w-5 h-5 text-slate-400 shrink-0" />
                </CardContent>
              </Card>
            ))}
            {filteredRichieste.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  Nessuna richiesta{richiesteFilter !== "all" ? " con questo filtro" : ""}.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        )}

        {canConfig && (
        <TabsContent value="impostazioni" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Notifiche richieste informazioni
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-w-lg">
              <p className="text-sm text-slate-500">
                Quando un utente invia &quot;Richiedi informazioni&quot; da Immobiliare o dal portale dedicato,
                riceverai un&apos;email a questo indirizzo. Le richieste restano sempre visibili nel tab Richieste.
              </p>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  Email destinatario
                </Label>
                <Input
                  type="email"
                  placeholder="es. immobiliare@esempio.it"
                  value={config.notification_email || ""}
                  onChange={(e) => setConfig((c) => ({ ...c, notification_email: e.target.value }))}
                />
              </div>
              {config.updated_at && (
                <p className="text-xs text-slate-400">
                  Ultimo aggiornamento: {formatDate(config.updated_at)}
                </p>
              )}
              <Button onClick={saveConfig} disabled={savingConfig}>
                {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Salva email
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canAccess && (
        <TabsContent value="accesso" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utenti Affidya con accesso a Immobiliare
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Cerca per nome o email..."
                  value={searchAccess}
                  onChange={(e) => setSearchAccess(e.target.value)}
                />
              </div>
              <div className="space-y-2 max-h-[480px] overflow-y-auto">
                {filteredAccess.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100">
                    <div>
                      <p className="font-medium text-sm">{u.name || u.email}</p>
                      <p className="text-xs text-slate-500">{u.email} · {u.facility_name}</p>
                    </div>
                    <Switch checked={u.immobiliare_enabled} onCheckedChange={(v) => toggleAccess(u.id, v)} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canViewers && (
        <TabsContent value="portale" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setViewerOpen(true)}>
              <UserPlus className="w-4 h-4 mr-1" />
              Nuovo utente portale
            </Button>
          </div>
          <div className="space-y-2">
            {viewers.map((v) => (
              <Card key={v.id}>
                <CardContent className="p-4 flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <p className="font-medium">{v.nome} {v.cognome}</p>
                    <p className="text-sm text-slate-500">{v.email}</p>
                    {v.is_active === false && (
                      <Badge variant="secondary" className="mt-1 text-xs">Disattivato</Badge>
                    )}
                    {v.must_change_password && v.is_active !== false && (
                      <Badge variant="outline" className="mt-1 text-xs ml-1">Password da cambiare</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={v.is_active === false || sendingAccessEmailId === v.id}
                      onClick={() => openSendAccessEmailDialog(v)}
                      title="Invia email con password temporanea e link di accesso"
                    >
                      {sendingAccessEmailId === v.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-1" />
                          Invia accesso
                        </>
                      )}
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteViewer(v.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {viewers.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">
                Nessun utente portale. Creane uno per l&apos;accesso a /portale-immobiliare/login
              </p>
            )}
          </div>
        </TabsContent>
        )}
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica immobile" : "Nuovo immobile"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-3">
              <div><Label>Nome</Label><Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div>
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Comune</Label><Input value={editForm.location} onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))} /></div>
                <div><Label>Provincia</Label><Input value={editForm.provincia} onChange={(e) => setEditForm((f) => ({ ...f, provincia: e.target.value }))} /></div>
              </div>
              <div><Label>Indirizzo</Label><Input value={editForm.address} onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Superficie mq</Label><Input type="number" value={editForm.sqm} onChange={(e) => setEditForm((f) => ({ ...f, sqm: e.target.value }))} /></div>
                <div><Label>Posti letto</Label><Input type="number" value={editForm.posti_letto} onChange={(e) => setEditForm((f) => ({ ...f, posti_letto: e.target.value }))} /></div>
              </div>
              <div><Label>Canone affitto</Label><Input value={editForm.rent_range} onChange={(e) => setEditForm((f) => ({ ...f, rent_range: e.target.value }))} /></div>
              <div><Label>Prezzo vendita</Label><Input type="number" value={editForm.prezzo_vendita} onChange={(e) => setEditForm((f) => ({ ...f, prezzo_vendita: e.target.value }))} /></div>
              <div><Label>Descrizione</Label><Textarea value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: v }))} />
                <Label>Attivo (visibile agli utenti)</Label>
              </div>
            </div>

            {editId && (
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Image className="w-4 h-4 text-orange-600" />
                    Foto ({editPhotos.length})
                  </Label>
                  <div className="flex gap-2">
                    {editZohoId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={uploadingPhoto}
                        onClick={() => syncPropertyMedia(editId)}
                      >
                        {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                        Da Zoho
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={uploadingPhoto}
                      onClick={() => editPhotoInputRef.current?.click()}
                    >
                      {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      Carica
                    </Button>
                  </div>
                </div>

                {editPhotos.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {editPhotos.map((photo) => (
                      <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                        <img
                          src={photoUrl(photo)}
                          alt={photo.file_name}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 p-1 rounded bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => deletePhoto(editId, photo.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 text-center">
                    Nessuna foto. Carica immagini o usa &quot;Da Zoho&quot; se l&apos;immobile proviene da Zoho.
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 border-t pt-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-600" />
                    Planimetria
                  </Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => editPlanInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-1" />
                    {editPlanimetria ? "Sostituisci" : "Carica PDF"}
                  </Button>
                </div>
                {editPlanimetria && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {editPlanimetria.name || "Planimetria.pdf"}
                  </p>
                )}
              </div>
            )}

            {!editId && (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                Salva prima l&apos;immobile per poter aggiungere foto e planimetria.
              </p>
            )}
          </div>

          <input
            ref={editPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (editId && files.length) uploadPhotos(editId, files);
              e.target.value = "";
            }}
          />
          <input
            ref={editPlanInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (editId && file) uploadPlanimetria(editId, file);
              e.target.value = "";
            }}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Chiudi</Button>
            <Button onClick={saveProperty}>{editId ? "Salva modifiche" : "Crea e aggiungi foto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedRichiesta} onOpenChange={() => setSelectedRichiesta(null)}>
        <DialogContent className="max-w-lg">
          {selectedRichiesta && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedRichiesta.property_name || "Richiesta immobile"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-slate-500">Stato</p>
                    <Badge className={selectedRichiesta.status === "nuovo" ? "bg-orange-600" : ""}>
                      {selectedRichiesta.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Data</p>
                    <p>{formatDate(selectedRichiesta.created_at)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-slate-500">Motivo</p>
                  <p className="font-medium">{selectedRichiesta.motivo_label}</p>
                </div>
                <div>
                  <p className="text-slate-500">Da</p>
                  <p>{selectedRichiesta.nome}</p>
                  <p>{selectedRichiesta.email}</p>
                  {selectedRichiesta.telefono && <p>{selectedRichiesta.telefono}</p>}
                </div>
                <div>
                  <p className="text-slate-500">Struttura / origine</p>
                  <p>{selectedRichiesta.struttura}</p>
                </div>
                {selectedRichiesta.richieste && (
                  <div>
                    <p className="text-slate-500">Messaggio</p>
                    <p className="whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{selectedRichiesta.richieste}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                {selectedRichiesta.status === "nuovo" && (
                  <Button variant="outline" onClick={() => updateRichiestaStatus(selectedRichiesta.id, "letto")}>
                    Segna come letta
                  </Button>
                )}
                {selectedRichiesta.status !== "gestito" && (
                  <Button onClick={() => updateRichiestaStatus(selectedRichiesta.id, "gestito")}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Segna come gestita
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente portale</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Email</Label><Input type="email" value={viewerForm.email} onChange={(e) => setViewerForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Password</Label><Input type="password" value={viewerForm.password} onChange={(e) => setViewerForm((f) => ({ ...f, password: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input value={viewerForm.nome} onChange={(e) => setViewerForm((f) => ({ ...f, nome: e.target.value }))} /></div>
              <div><Label>Cognome</Label><Input value={viewerForm.cognome} onChange={(e) => setViewerForm((f) => ({ ...f, cognome: e.target.value }))} /></div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="viewer-send-email"
                checked={viewerForm.sendAccessEmail}
                onCheckedChange={(checked) =>
                  setViewerForm((f) => ({ ...f, sendAccessEmail: checked === true }))
                }
              />
              <Label htmlFor="viewer-send-email" className="text-sm font-normal cursor-pointer">
                Invia subito email di accesso con la password impostata
              </Label>
            </div>
          </div>
          <DialogFooter><Button onClick={createViewer}>Crea utente</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={accessEmailOpen}
        onOpenChange={(open) => {
          setAccessEmailOpen(open);
          if (!open) {
            setAccessEmailViewer(null);
            setAccessEmailPassword("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia email di accesso</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <p className="text-sm text-slate-600">
              L&apos;email conterrà la password temporanea e il link al portale per{" "}
              <strong>{accessEmailViewer?.email}</strong>.
            </p>
            <div>
              <Label>Password impostata per l&apos;utente</Label>
              <Input
                type="password"
                value={accessEmailPassword}
                onChange={(e) => setAccessEmailPassword(e.target.value)}
                placeholder="Inserisci la password attuale dell'utente"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessEmailOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={sendViewerAccessEmail}
              disabled={!accessEmailPassword || sendingAccessEmailId === accessEmailViewer?.id}
            >
              {sendingAccessEmailId === accessEmailViewer?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-1" />
                  Invia email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
